// AnnuncioRisultato.tsx — gli annunci AdSense (web) per chi è in Base. Mai sopra i video. Se il server dice di no, o
// AdSense non è configurato, non mostra nulla (niente spazi vuoti).
// v045 (il proprietario: «la pubblicità ci sia nei posti giusti, che non dia fastidio, ma che ci permetta di
// guadagnarci anche con gli utenti base»): conta il piano di CHI guarda (prima: se uno dei due era abbonato niente
// pubblicità per nessuno, e un amico Gold «copriva» tutti). Tre posti: fine partita (`risultato`), mentre si aspetta
// l'altro (`attesa`) e la pagina «prima di giocare» (`prima`, vedi PubblicitaPrima.tsx).
// v046: nelle app Android/iOS al posto di AdSense c'è AdMob (servizi/admob.ts): un banner nativo in fondo allo
// schermo, sopra la pagina, finché il componente c'è (quando sparisce si toglie). Qui resta solo uno spazio alto
// quanto il banner, così la fine della pagina si può ancora vedere. Il posto `prima` nelle app è un annuncio a tutto
// schermo (PubblicitaPrima.tsx), quindi qui non mostra niente. Sul web nulla cambia.
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { caricaAdsense, pubblicitaAttiva } from "../servizi/pubblicita.ts";
import { pubblicitaConsentita, useAccount } from "../servizi/account.ts";
import { eTesto, leggiImpostazione } from "../servizi/impostazioni.ts";
import { IMP_APP } from "../costanti.ts";
import { NATIVA } from "../servizi/nativo.ts";
import { admobDisponibile, bannerAdMob } from "../servizi/admob.ts";

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

export type PostoAnnuncio = "risultato" | "attesa" | "prima";
const SLOT: Record<PostoAnnuncio, string> = {
  risultato: IMP_APP.adsenseSlotRisultato,
  attesa: IMP_APP.adsenseSlotAttesa,
  prima: IMP_APP.adsenseSlotPrima,
};

/** Lo slot AdSense del posto (vuoto = quel posto è spento). */
export const slotAnnuncio = (posto: PostoAnnuncio): string => leggiImpostazione(SLOT[posto], "", eTesto);

/**
 * Un annuncio. Con `codice` e `token` (in partita) decide il server dal piano di chi guarda; senza (prima di giocare)
 * basta il piano dell'account (chi non ha l'account è Base). `onPronto(true)` quando l'annuncio è caricato.
 */
export function Annuncio({ posto, codice, token, onPronto }: { posto: PostoAnnuncio; codice?: string; token?: string | null; onPronto?: (si: boolean) => void }) {
  const { t } = useTranslation();
  const account = useAccount();
  const [mostra, setMostra] = useState(false);
  const rif = useRef<HTMLModElement>(null);
  const spinto = useRef(false);
  const abbonato = account.tipo === "registrato" && account.io.piano !== "base";
  const client = leggiImpostazione(IMP_APP.adsenseClient, "", eTesto);
  const slot = slotAnnuncio(posto);

  useEffect(() => {
    const disponibile = NATIVA ? posto !== "prima" && admobDisponibile("banner") : !!slot;
    if (!pubblicitaAttiva() || abbonato || !disponibile || (codice !== undefined && !token)) {
      onPronto?.(false);
      return;
    }
    let chiuso = false;
    const consentita = codice !== undefined && token ? pubblicitaConsentita(codice, token) : Promise.resolve(true);
    consentita
      .then(async (si) => {
        if (chiuso) return;
        const ok = si && (NATIVA ? await bannerAdMob(posto as "risultato" | "attesa", true) : await caricaAdsense());
        if (chiuso) return;
        setMostra(ok);
        onPronto?.(ok);
      })
      .catch(() => !chiuso && onPronto?.(false)); // senza risposta niente annuncio
    return () => {
      chiuso = true;
      if (NATIVA && posto !== "prima") void bannerAdMob(posto, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codice, token, abbonato, slot]);

  useEffect(() => {
    if (NATIVA || !mostra || spinto.current || !rif.current) return;
    spinto.current = true;
    try {
      (window.adsbygoogle = window.adsbygoogle ?? []).push({});
    } catch {
      // annuncio non disponibile: lo spazio resta vuoto
    }
  }, [mostra]);

  if (!mostra) return null;
  // app: il banner è nativo, sopra la pagina; qui solo lo spazio per non coprire la fine della pagina
  if (NATIVA) return <div className={`annuncio-admob annuncio-${posto}`} aria-hidden="true" style={{ blockSize: "var(--ld-banner-admob, 0px)" }} />;
  return (
    <aside className={`annuncio-risultato annuncio-${posto}`} aria-label={t("pubblicita.etichetta")}>
      <span className="annuncio-etichetta">{t("pubblicita.etichetta")}</span>
      <ins
        ref={rif}
        className="adsbygoogle"
        style={{ display: "block" }}
        data-ad-client={client}
        data-ad-slot={slot}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </aside>
  );
}

/** L'annuncio di fine partita (com'era prima della v045). */
export function AnnuncioRisultato({ codice, token }: { codice: string; token: string | null }) {
  return <Annuncio posto="risultato" codice={codice} token={token} />;
}
